import React, { useState, useEffect } from 'react';
import RolesPicker from './RolesPicker';
import PermissionsPicker from './PermissionsPicker';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import useApi from '../hooks/useApi';
import Skeleton from '@mui/material/Skeleton';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';

const RolesAndPermissionsPicker = ({ isFirstUser = false, selectedRoleId, selectedPermissions, setSelectedRoleId, setSelectedPermissions }) => {
    const [isCustomRole, setIsCustomRole] = useState(selectedPermissions.length > 0);
    const [isLoading, setIsLoading] = useState(true);
    const [internalRoleId, setInternalRoleId] = useState(selectedRoleId);
    const [availableRoles, setAvailableRoles] = useState([]);
    const [availablePermissions, setAvailablePermissions] = useState([]);
    const [finalPermissions, setFinalPermissions] = useState([]);
    const { handleGet } = useApi();

    const handleGetAvailableRoles = async () => {
        const response = await handleGet('roles');
        const data = await response.json();
        setAvailableRoles(data);
    };

    const handleGetPermissions = async () => {
        const response = await handleGet('permissions');
        const data = await response.json();
        setAvailablePermissions(data);
    };

    useEffect(() => {
        (async () => {
            await handleGetAvailableRoles();
            await handleGetPermissions();
            setIsLoading(false);
        })();
    }, []);

    useEffect(() => {
        if (isLoading) {
            return;
        }

        if (isCustomRole) {
            if (selectedRoleId) {
                const permissions = availableRoles.find(r => r.id === selectedRoleId)?.permissions;
                setSelectedPermissions(permissions ?? []);
                setSelectedRoleId(null);
            }
        }
        else {
            setSelectedRoleId(internalRoleId ?? availableRoles[0].id);
            setSelectedPermissions([]);
        }
    }, [isCustomRole, isLoading]);

    useEffect(() => {
        if (isLoading) {
            return;
        }

        const permissions = isCustomRole
            ? availablePermissions.filter(p => selectedPermissions.some(({ id }) => id === p.id))
            : availableRoles.find(r => r.id === selectedRoleId)?.permissions ?? [];

        setFinalPermissions(permissions);
    }, [isCustomRole, selectedPermissions, selectedRoleId, isLoading]);

    if (isLoading) {
        return (
            <Skeleton sx={{ mt: 2, transform: 'unset' }} />
        );
    }

    return (
        <>
            <Alert severity="info" sx={{ mt: 2, mb: 2 }}>
                <AlertTitle>Selected Permissions</AlertTitle>
                {finalPermissions.length > 0 ? `${finalPermissions.map(p => p.name).join(', ')}.` : 'No permissions selected.'}
            </Alert>
            <FormControlLabel label="Custom Role" control={(
                <Switch checked={isCustomRole} onChange={e => setIsCustomRole(e.target.checked)} />
            )} />
            {!isCustomRole && (
                <RolesPicker
                    availableRoles={availableRoles}
                    selectedRoleId={selectedRoleId}
                    onChange={id => {
                        setSelectedRoleId(id);
                        setInternalRoleId(id);
                    }}
                />
            )}
            {isCustomRole && (
                <PermissionsPicker
                    isFirstUser={isFirstUser}
                    availablePermissions={availablePermissions}
                    selectedPermissions={selectedPermissions}
                    onChange={setSelectedPermissions}
                />
            )}
        </>
    );
};

export default RolesAndPermissionsPicker;